import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSuperApp } from "../hooks/useSuperApp";
import { getToken } from "../api/client";
import { decodeJwtPayload } from "../utils/jwtUtils";
import { setTrackingUser } from "../utils/tracking";
import { onRouteChange } from "../utils/visitTracking";
import { reloadIfUpdateReady } from "../utils/versionCheck";

/**
 * The candidate id of whoever is signed in. The JWT is the authority: its `id` claim is
 * the candidate id on every candidate token (phone/OTP, Toki, Hambi — see hrSystemServer
 * JwtTokenUtils.generateCandidateTokenPair). The user objects are not: a Toki user is the
 * raw profile Toki returned (phoneNo, name…), with no candidate id on it at all.
 */
const candidateIdFromToken = () => {
  const payload = decodeJwtPayload(getToken());
  if (!payload || !Array.isArray(payload.authorities)) return null;
  if (!payload.authorities.includes("CANDIDATE")) return null;
  return payload.id ?? null;
};

/**
 * Feeds the two things analytics needs from React: who is signed in (every event carries
 * the candidate id, and GA4 gets it as user_id) and which page is open (route_view /
 * visit_end). Renders nothing. Mounted once, inside the Router, ahead of the routes so its
 * effects run before any page's own tracking.
 */
const TrackingRoot = () => {
  const { pathname } = useLocation();
  const { currentUser } = useSuperApp();
  // Re-read whenever the signed-in user changes (login, logout, Toki/Hambi re-auth).
  // A phone-login user's object carries the candidate id as `id`, used if the token
  // cannot be read.
  const candidateId = currentUser
    ? candidateIdFromToken() ?? currentUser.candidateId ?? (currentUser.phoneNo ? null : currentUser.id) ?? null
    : null;

  useEffect(() => {
    setTrackingUser(candidateId);
  }, [candidateId]);

  useEffect(() => {
    onRouteChange(pathname);
    // A newer deploy was noticed while this page was open: load it now, on a navigation,
    // where nothing on screen is lost
    reloadIfUpdateReady();
  }, [pathname]);

  return null;
};

export default TrackingRoot;
